import { Brain, Database, Loader2, ScanText, Sparkles, Wand2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { LoadingState } from "@/components/ui/multi-step-loader";

interface QuickProcessingBannerProps {
  draftId: number | string;
  status: string; // e.g., 'quick_processing' | 'quick_ready' | 'quick_error'
  processing: boolean;
  className?: string;
  steps?: LoadingState[];
}

export default function QuickProcessingBanner({ draftId, status, processing, className, steps }: QuickProcessingBannerProps) {
  const defaultSteps: LoadingState[] = useMemo(
    () => [
      { text: "El procesado puede tardar unos minutos…", icon: Loader2 },
      { text: "Leyendo y normalizando descripciones…", icon: ScanText },
      { text: "Traduciendo productos…", icon: Wand2 },
      { text: "Haciendo match con la base de productos…", icon: Database },
      { text: "Estimando cantidades y unidades…", icon: Sparkles },
      { text: "Detectando marcas y códigos…", icon: ScanText },
      { text: "Preparando sugerencias para revisión…", icon: Brain },
    ],
    []
  );

  const usedSteps = steps ?? defaultSteps;
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!processing) return;
    setIdx(0);
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % usedSteps.length);
    }, 1600);
    return () => clearInterval(t);
  }, [processing, usedSteps.length]);

  const CurrentIcon = usedSteps[idx]?.icon ?? Loader2;

  return (
    <Alert
      className={
        `relative overflow-hidden border ${
          processing
            ? 'border-primary/40 bg-gradient-to-r from-indigo-50 via-violet-50 to-fuchsia-50'
            : status === 'quick_error'
            ? 'border-red-300 bg-red-50'
            : status === 'quick_ready'
            ? 'border-emerald-300 bg-emerald-50'
            : 'border-border'
        } ${className ?? ''}`
      }
    >
      {/* Keyframes for indeterminate progress bar */}
      <style>{`@keyframes prg-indeterminate { 0% { transform: translateX(-30%); } 100% { transform: translateX(100%); } }`}</style>

      {processing && (
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-400 via-fuchsia-400 to-amber-400" />
      )}

      <AlertTitle className="flex items-center gap-2">
        <Sparkles className="h-4 w-4" /> Solicitud rápida en borrador
      </AlertTitle>
      <AlertDescription className="flex flex-col gap-3 text-sm">
        <div className="flex flex-wrap items-center gap-3">
          <span><strong>ID:</strong> {draftId}</span>
          <span className="inline-flex items-center gap-2">
            <strong>Estado:</strong>
            {processing ? (
              <span className="inline-flex items-center gap-2 text-primary">
                <Loader2 className="h-4 w-4 animate-spin" /> Procesando con IA…
              </span>
            ) : status === 'quick_ready' ? (
              <span className="inline-flex items-center gap-2 text-emerald-700">✅ Listo para editar</span>
            ) : status === 'quick_error' ? (
              <span className="inline-flex items-center gap-2 text-red-600">⚠️ Error en procesamiento</span>
            ) : (
              <span className="inline-flex items-center gap-2 text-muted-foreground">-</span>
            )}
          </span>
        </div>

        {processing && (
          <div className="rounded-md border bg-muted/40 p-3">
            <div className="flex items-center gap-2">
              <CurrentIcon className="h-4 w-4 text-primary" />
              <span className="font-medium">{usedSteps[idx]?.text}</span>
            </div>
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
              <div className="h-full w-1/3 rounded-full bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-emerald-500" style={{ animation: 'prg-indeterminate 1.6s ease-in-out infinite alternate' }} />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Estamos procesando con IA. Puede llevar un momento y quizás necesites ajustar algunos datos luego.
            </p>
          </div>
        )}

        {!processing && status === 'quick_error' && (
          <p className="text-xs text-red-700">
            Sugerencia: intenta nuevamente o ajusta el texto de entrada.
          </p>
        )}
      </AlertDescription>
    </Alert>
  );
}

